import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { Device } from '@/views/Devices/models/device'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'

interface ReportMetrics {
    totalDevices: number
    activeDevices: number
    totalMeasurements: number
    analyzedMeasurements: number
    totalNewRoots: number
    averageGrowthPercentage: number
    averageOxygen: number
    averageBattery: number
}

function addHeader(doc: jsPDF, title: string, subtitle: string) {
    doc.setFillColor(19, 76, 110) // #134c6e primary
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 35, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.text(title, 14, 16)
    doc.setFontSize(10)
    doc.text(subtitle, 14, 26)
    doc.setTextColor(0, 0, 0)
}

function addFooter(doc: jsPDF) {
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150)
        const date = new Date().toLocaleDateString('es-CL')
        doc.text(`Generado el ${date} - Wipitech Fotorizotron Panel`, 14, doc.internal.pageSize.getHeight() - 10)
        doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.getWidth() - 40, doc.internal.pageSize.getHeight() - 10)
    }
}

function calcMetrics(devices: Device[], measurements: DeviceSample[]): ReportMetrics {
    const managed = measurements.filter(m => m.managed)
    return {
        totalDevices: devices.length,
        activeDevices: devices.filter(d => d.active).length,
        totalMeasurements: measurements.length,
        analyzedMeasurements: managed.length,
        totalNewRoots: managed.reduce((s, m) => s + m.newRootsCount, 0),
        averageGrowthPercentage: managed.length > 0
            ? Math.round(managed.reduce((s, m) => s + m.growthPercent, 0) / managed.length * 10) / 10
            : 0,
        averageOxygen: 0,
        averageBattery: 0,
    }
}

// ─── Reporte de Crecimiento ───
export function generateGrowthReport(devices: Device[], measurements: DeviceSample[]) {
    const doc = new jsPDF()
    const metrics = calcMetrics(devices, measurements)

    addHeader(doc, 'Reporte de Crecimiento de Raíces', `Dispositivos: ${metrics.totalDevices} | Mediciones analizadas: ${metrics.analyzedMeasurements}`)

    let y = 45

    // Resumen
    doc.setFontSize(14)
    doc.text('Resumen General', 14, y)
    y += 8

    doc.setFontSize(10)
    doc.text(`Total de dispositivos: ${metrics.totalDevices} (${metrics.activeDevices} activos)`, 14, y); y += 6
    doc.text(`Total de mediciones: ${metrics.totalMeasurements} (${metrics.analyzedMeasurements} analizadas)`, 14, y); y += 6
    doc.text(`Crecimiento promedio: ${metrics.averageGrowthPercentage}%`, 14, y); y += 6
    doc.text(`Total raíces nuevas detectadas: ${metrics.totalNewRoots}`, 14, y); y += 12

    // Tabla de mediciones analizadas
    const managed = measurements
        .filter(m => m.managed)
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())

    if (managed.length > 0) {
        doc.setFontSize(14)
        doc.text('Detalle de Mediciones Analizadas', 14, y)
        y += 4

        const rows = managed.map(m => {
            const device = devices.find(d => d.id === m.deviceId)
            return [
                device?.name || m.deviceId.slice(0, 12),
                new Date(m.time).toLocaleDateString('es-CL'),
                `${m.growthPercent}%`,
                String(m.newRootsCount),
                `${m.rootHealthScore}/10`,
                m.rootColor || '-',
                m.observation?.slice(0, 40) || '-'
            ]
        })

        autoTable(doc, {
            startY: y,
            head: [['Dispositivo', 'Fecha', 'Crecimiento', 'Raíces', 'Salud', 'Color', 'Observación']],
            body: rows,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [19, 76, 110] },
        })
    }

    addFooter(doc)
    doc.save('Reporte_Crecimiento.pdf')
}

// ─── Reporte de Sensores ───
export function generateSensorReport(devices: Device[], measurements: DeviceSample[]) {
    const doc = new jsPDF()

    addHeader(doc, 'Informe de Sensores', `Dispositivos: ${devices.length}`)

    let y = 45

    doc.setFontSize(14)
    doc.text('Estado de Dispositivos', 14, y)
    y += 4

    const rows = devices.map(d => {
        let metadata: Record<string, any> = {}
        try { metadata = typeof d.metadata === 'string' ? JSON.parse(d.metadata) : d.metadata || {} } catch { /* */ }
        const deviceSamples = measurements.filter(m => m.deviceId === d.id)
        return [
            d.name || d.code,
            d.active ? 'Activo' : 'Inactivo',
            String(deviceSamples.length),
            String(deviceSamples.filter(m => m.managed).length),
            metadata.status || '-',
            d.periodicity ? `${d.periodicity} min` : '-',
            d.updatedAt ? new Date(d.updatedAt).toLocaleDateString('es-CL') : '-'
        ]
    })

    autoTable(doc, {
        startY: y,
        head: [['Dispositivo', 'Estado', 'Muestras', 'Analizadas', 'Conexión', 'Periodicidad', 'Últ. Actualización']],
        body: rows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [19, 76, 110] },
    })

    addFooter(doc)
    doc.save('Informe_Sensores.pdf')
}

// ─── Reporte de Batería ───
export function generateBatteryReport(devices: Device[]) {
    const doc = new jsPDF()

    addHeader(doc, 'Reporte de Estado de Batería', `Dispositivos monitoreados: ${devices.length}`)

    let y = 45

    doc.setFontSize(14)
    doc.text('Niveles de Batería por Dispositivo', 14, y)
    y += 4

    const rows = devices.map(d => {
        let metadata: Record<string, any> = {}
        try { metadata = typeof d.metadata === 'string' ? JSON.parse(d.metadata) : d.metadata || {} } catch { /* */ }
        const battery = metadata.batteryLevel != null ? `${metadata.batteryLevel}%` : 'Sin datos'
        const signal = metadata.signalStrength != null ? `${metadata.signalStrength}%` : '-'
        const lastMaintenance = metadata.lastMaintenanceDate
            ? new Date(metadata.lastMaintenanceDate).toLocaleDateString('es-CL')
            : '-'
        return [
            d.name || d.code,
            d.active ? 'Activo' : 'Inactivo',
            battery,
            signal,
            lastMaintenance,
            d.updatedAt ? new Date(d.updatedAt).toLocaleDateString('es-CL') : '-'
        ]
    })

    autoTable(doc, {
        startY: y,
        head: [['Dispositivo', 'Estado', 'Batería', 'Señal', 'Últ. Mantención', 'Últ. Actualización']],
        body: rows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [19, 76, 110] },
    })

    addFooter(doc)
    doc.save('Reporte_Bateria.pdf')
}

// ─── Reporte Comparativo por Dispositivo ───
export function generateComparativeReport(devices: Device[], measurements: DeviceSample[]) {
    const doc = new jsPDF()

    addHeader(doc, 'Análisis Comparativo por Dispositivo', `${devices.length} dispositivos comparados`)

    let y = 45

    doc.setFontSize(14)
    doc.text('Comparativa de Rendimiento', 14, y)
    y += 4

    const rows = devices.map(d => {
        const samples = measurements.filter(m => m.deviceId === d.id)
        const managed = samples.filter(m => m.managed)
        const avgGrowth = managed.length > 0
            ? Math.round(managed.reduce((s, m) => s + m.growthPercent, 0) / managed.length * 10) / 10
            : 0
        const avgHealth = managed.length > 0
            ? Math.round(managed.reduce((s, m) => s + m.rootHealthScore, 0) / managed.length * 10) / 10
            : 0
        const totalRoots = managed.reduce((s, m) => s + m.newRootsCount, 0)
        const completion = samples.length > 0 ? Math.round(managed.length / samples.length * 100) : 0

        return [
            d.name || d.code,
            String(samples.length),
            String(managed.length),
            `${completion}%`,
            `${avgGrowth}%`,
            `${avgHealth}/10`,
            String(totalRoots)
        ]
    })

    autoTable(doc, {
        startY: y,
        head: [['Dispositivo', 'Muestras', 'Analizadas', 'Completado', 'Crec. Prom.', 'Salud Prom.', 'Raíces']],
        body: rows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [19, 76, 110] },
    })

    addFooter(doc)
    doc.save('Analisis_Comparativo.pdf')
}

// ─── Reporte Ejecutivo ───
export function generateExecutiveReport(devices: Device[], measurements: DeviceSample[]) {
    const doc = new jsPDF()
    const metrics = calcMetrics(devices, measurements)
    const managed = measurements.filter(m => m.managed)

    addHeader(doc, 'Dashboard Ejecutivo', `Resumen general del sistema Fotorizotron`)

    let y = 45

    // KPIs
    doc.setFontSize(14)
    doc.text('Indicadores Clave', 14, y); y += 10

    const kpis = [
        ['Dispositivos Totales', String(metrics.totalDevices)],
        ['Dispositivos Activos', String(metrics.activeDevices)],
        ['Tasa de Actividad', `${metrics.totalDevices > 0 ? Math.round(metrics.activeDevices / metrics.totalDevices * 100) : 0}%`],
        ['Mediciones Totales', String(metrics.totalMeasurements)],
        ['Mediciones Analizadas', String(metrics.analyzedMeasurements)],
        ['Tasa de Análisis', `${metrics.totalMeasurements > 0 ? Math.round(metrics.analyzedMeasurements / metrics.totalMeasurements * 100) : 0}%`],
        ['Crecimiento Promedio', `${metrics.averageGrowthPercentage}%`],
        ['Total Raíces Detectadas', String(metrics.totalNewRoots)],
    ]

    autoTable(doc, {
        startY: y,
        head: [['Indicador', 'Valor']],
        body: kpis,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [19, 76, 110] },
        columnStyles: { 0: { fontStyle: 'bold' } },
    })

    y = (doc as any).lastAutoTable.finalY + 12

    // Top dispositivos
    if (managed.length > 0) {
        doc.setFontSize(14)
        doc.text('Top Dispositivos por Crecimiento', 14, y); y += 4

        const deviceStats = devices.map(d => {
            const dm = managed.filter(m => m.deviceId === d.id)
            const avg = dm.length > 0 ? Math.round(dm.reduce((s, m) => s + m.growthPercent, 0) / dm.length * 10) / 10 : 0
            return { name: d.name || d.code, avg, count: dm.length }
        }).sort((a, b) => b.avg - a.avg).slice(0, 10)

        autoTable(doc, {
            startY: y,
            head: [['Dispositivo', 'Crec. Promedio', 'Mediciones']],
            body: deviceStats.map(d => [d.name, `${d.avg}%`, String(d.count)]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [19, 76, 110] },
        })
    }

    addFooter(doc)
    doc.save('Dashboard_Ejecutivo.pdf')
}
